
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ConsultModal from '../../components/feature/ConsultModal';
import ScrollToTop from '../../components/feature/ScrollToTop';
import LanguageGuard from '../../components/feature/LanguageGuard';
import SeoHead from '../../components/feature/SeoHead';
import BlogCard from './components/BlogCard';
import { blogPosts } from '../../mocks/blogPosts';
import { getLangPath, getBlogDetailPath, type Language } from '../../router/langRoutes';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://thedaeg.com';

const BlogDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { i18n } = useTranslation();
  const currentLang = i18n.language as Language;

  const post = blogPosts.find((p) => p.id === slug);
  const relatedPosts = blogPosts.filter((p) => p.id !== slug).slice(0, 3);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const articleSchema = post
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        image: post.image,
        datePublished: post.date,
        dateModified: post.date,
        author: {
          '@type': 'Organization',
          name: 'The Daeg International Trade',
          url: SITE_URL,
        },
        publisher: {
          '@type': 'Organization',
          name: 'The Daeg International Trade',
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/logo.png`,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}${getBlogDetailPath(post.id, currentLang)}`,
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Trang chủ', item: `${SITE_URL}${getLangPath('home', currentLang)}` },
            { '@type': 'ListItem', position: 2, name: 'Định Cư Latvia', item: `${SITE_URL}${getLangPath('latvia', currentLang)}` },
            { '@type': 'ListItem', position: 3, name: 'Blog', item: `${SITE_URL}${getLangPath('latviaBlog', currentLang)}` },
            { '@type': 'ListItem', position: 4, name: post.title, item: `${SITE_URL}${getBlogDetailPath(post.id, currentLang)}` },
          ],
        },
      }
    : undefined;

  if (!post) {
    return (
      <LanguageGuard>
        <div className="min-h-screen bg-white">
          <header>
            <Navbar onConsultClick={() => setIsModalOpen(true)} />
          </header>
          <main className="pt-24 sm:pt-28 md:pt-32 pb-16 text-center px-5">
            <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
              <i className="ri-file-unknow-line text-3xl md:text-4xl text-gray-400"></i>
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Bài viết không tồn tại</h1>
            <p className="text-sm text-gray-500 mb-6">Bài viết bạn tìm kiếm không có hoặc đã bị xóa.</p>
            <Link
              to={getLangPath('latviaBlog', currentLang)}
              className="inline-flex items-center px-5 py-2.5 bg-gold-600 text-white text-sm font-medium rounded-lg hover:bg-gold-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Quay lại Blog
            </Link>
          </main>
          <Footer />
          <ConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </div>
      </LanguageGuard>
    );
  }

  return (
    <LanguageGuard>
      <div className="min-h-screen bg-white">
        <SeoHead
          title={`${post.title} | Tin Tức Định Cư Latvia - The Daeg`}
          description={post.excerpt}
          keywords={`${post.category}, định cư Latvia, ${post.title.split(' ').slice(0, 5).join(', ')}`}
          canonicalPath={getBlogDetailPath(post.id, currentLang)}
          ogImage={post.image}
          schemaJson={articleSchema}
        />
        <header>
          <Navbar onConsultClick={() => setIsModalOpen(true)} />
        </header>
        <main>
          {/* Hero Image */}
          <div className="relative w-full h-60 sm:h-72 md:h-[420px] mt-16 md:mt-20">
            <img
              src={post.image}
              alt={`${post.title} - Định cư Latvia`}
              title={post.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
          </div>

          {/* Article Content */}
          <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 sm:-mt-16 md:-mt-24 relative z-10">
            <div className="bg-white rounded-xl md:rounded-2xl shadow-xl p-5 sm:p-7 md:p-10 border border-gray-100">
              {/* Breadcrumb */}
              <nav aria-label="Breadcrumb" className="mb-4 md:mb-6">
                <ol className="flex items-center flex-wrap text-xs md:text-sm text-gray-400 gap-y-1">
                  <li><Link to={getLangPath('home', currentLang)} className="hover:text-gold-600 transition-colors cursor-pointer">Trang chủ</Link></li>
                  <li className="mx-1.5">/</li>
                  <li><Link to={getLangPath('latvia', currentLang)} className="hover:text-gold-600 transition-colors cursor-pointer">Định Cư Latvia</Link></li>
                  <li className="mx-1.5">/</li>
                  <li><Link to={getLangPath('latviaBlog', currentLang)} className="hover:text-gold-600 transition-colors cursor-pointer">Blog</Link></li>
                  <li className="mx-1.5">/</li>
                  <li className="text-gray-600 truncate max-w-[180px] sm:max-w-[250px] md:max-w-none">{post.title}</li>
                </ol>
              </nav>

              {/* Category & Meta */}
              <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 mb-3 md:mb-5">
                <span className="px-3 py-1 bg-gold-50 text-gold-700 text-xs font-semibold rounded-full border border-gold-200">
                  {post.category}
                </span>
                <div className="flex items-center gap-2.5 sm:gap-3 text-[11px] sm:text-xs text-gray-400">
                  <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{post.date}</span>
                  <span className="flex items-center gap-1"><i className="ri-time-line"></i>{post.readTime}</span>
                  <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{post.views.toLocaleString()} lượt xem</span>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-5 md:mb-8 leading-snug font-serif">
                {post.title}
              </h1>

              {/* Content */}
              <div
                className="prose prose-sm sm:prose-base max-w-none text-gray-700 leading-relaxed
                  [&_h3]:text-base [&_h3]:sm:text-lg [&_h3]:md:text-xl [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:mt-6 [&_h3]:sm:mt-7 [&_h3]:md:mt-8 [&_h3]:mb-2.5 [&_h3]:md:mb-4 [&_h3]:font-serif
                  [&_p]:text-[13px] [&_p]:sm:text-sm [&_p]:md:text-base [&_p]:mb-3.5 [&_p]:md:mb-5 [&_p]:leading-relaxed
                  [&_strong]:text-gray-900"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              {/* Share & CTA */}
              <div className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2.5">Chia sẻ bài viết:</p>
                    <div className="flex items-center gap-2.5">
                      {['ri-facebook-fill', 'ri-linkedin-fill', 'ri-twitter-x-fill'].map((icon, i) => (
                        <a key={i} href="#" rel="nofollow" className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gold-100 hover:text-gold-700 transition-colors cursor-pointer text-gray-600">
                          <i className={`${icon} text-base`}></i>
                        </a>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="w-full sm:w-auto px-6 py-3 bg-gold-600 text-white text-sm font-semibold rounded-lg hover:bg-gold-700 transition-colors shadow-md whitespace-nowrap cursor-pointer text-center"
                  >
                    Đăng Ký Tư Vấn Miễn Phí
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Related Posts */}
          <section className="py-10 sm:py-12 md:py-16 bg-gray-50 mt-8 md:mt-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h4 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-5 md:mb-8 font-serif">
                <Link to={getLangPath('latviaBlog', currentLang)} className="hover:text-gold-700 transition-colors">
                  Bài Viết Liên Quan
                </Link>
              </h4>

              {/* Mobile */}
              <div className="flex flex-col gap-3 sm:hidden">
                {relatedPosts.map((rp) => (
                  <Link key={rp.id} to={getBlogDetailPath(rp.id, currentLang)}
                    className="group flex bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 cursor-pointer">
                    <div className="relative w-28 min-w-[112px] h-24 overflow-hidden flex-shrink-0">
                      <img src={rp.image} alt={`${rp.title} - Định cư Latvia`} title={rp.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm text-white text-[9px] font-medium rounded">
                        {rp.category}
                      </span>
                    </div>
                    <div className="flex flex-col justify-center p-3 min-w-0 flex-1">
                      <h3 className="text-[13px] font-bold text-gray-900 line-clamp-2 group-hover:text-gold-700 transition-colors leading-snug mb-1.5">
                        {rp.title}
                      </h3>
                      <div className="flex items-center gap-2 text-[10px] text-gray-400">
                        <span className="flex items-center gap-0.5"><i className="ri-calendar-line"></i>{rp.date}</span>
                        <span className="flex items-center gap-0.5"><i className="ri-eye-line"></i>{rp.views.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center pr-3 flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-gold-600 transition-colors text-lg"></i>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Desktop */}
              <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
                {relatedPosts.map((rp) => (
                  <BlogCard key={rp.id} {...rp} />
                ))}
              </div>

              <div className="text-center mt-6 sm:mt-8 md:mt-10">
                <Link to={getLangPath('latviaBlog', currentLang)}
                  className="inline-flex items-center px-5 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:border-gold-400 hover:text-gold-700 transition-colors cursor-pointer whitespace-nowrap">
                  Xem tất cả bài viết
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <ConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        <ScrollToTop />
      </div>
    </LanguageGuard>
  );
};

export default BlogDetailPage;
